import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { CustomLogicPopupContainer } from './styles';
import { Menu } from 'antd';
import { MenuItem } from '../../../components/PageResizeDropdown/styles';
import { useTranslation } from 'react-i18next';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../../context/complexDocumentContext';
import { LOGIC_LIST_DESC } from '../../ComplexDocumentDesign/translationValues';

const NAMESPACES = ['CREATE_VARIABLE_POPUP', 'LOGIC_LIST_DESC'];
const Keys = {
  TAB: 9,
  ENTER: 13,
  ESCAPE: 27,
  UP: 38,
  DOWN: 40,
};

const LogicMentionPopup = props => {
  const { quill, target, searchTerm, onHide, onVariableSelect, position, uuid } = props;
  const { t } = useTranslation(NAMESPACES);
  const scrollRef = useRef();
  const [active, setActive] = useState(0);
  const designEditableSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EDITABLE], []);
  const [isEditable] = useDocumentContext(designEditableSelector, `TextBlockWithQuillRenderer`);

  const LOGIC_LIST = useMemo(() => [
    {
      id: 1,
      value: 'current_date',
      placeHolderText: 'current_date',
      contents: [
        {
          content: 'current-dateTime()',
          contentType: 'PARAGRAPH',
          style: {
            isVariable: false,
            dateFormat: '[D01]-[M01]-[Y0001]',
          },
        },
        {
          content: '\n',
          contentType: 'LINE_BREAK',
          style: {
            alignment: 'left',
            lineSpacing: 1.5,
            indent: 0,
          },
        },
      ],
      description: t(LOGIC_LIST_DESC.CURRENT_DATE.key, LOGIC_LIST_DESC.CURRENT_DATE.value),
    },
    {
      id: 2,
      value: 'current_time',
      placeHolderText: 'current_time',
      contents: [
        {
          content: 'current-dateTime()',
          contentType: 'PARAGRAPH',
          style: {
            isVariable: false,
            dateFormat: '[H01]:[m01]:[s01] [Z]',
          },
        },
        {
          content: '\n',
          contentType: 'LINE_BREAK',
          style: {
            alignment: 'left',
            lineSpacing: 1.5,
            indent: 0,
          },
        },
      ],
      description: t(LOGIC_LIST_DESC.CURRENT_TIME.key, LOGIC_LIST_DESC.CURRENT_TIME.value),
    },
    {
      id: 3,
      value: 'current_date_time',
      placeHolderText: 'current_date_time',
      contents: [
        {
          content: 'current-dateTime()',
          contentType: 'PARAGRAPH',
          style: {
            isVariable: false,
            dateFormat: '[D01]-[M01]-[Y0001] [H01]:[m01]:[s01] [Z]',
          },
        },
        {
          content: '\n',
          contentType: 'LINE_BREAK',
          style: {
            alignment: 'left',
            lineSpacing: 1.5,
            indent: 0,
          },
        },
      ],
      description: t(LOGIC_LIST_DESC.CURRENT_DATE_TIME.key, LOGIC_LIST_DESC.CURRENT_DATE_TIME.value),
    },
    {
      id: 4,
      value: 'current_user',
      placeHolderText: 'current_user',
      contents: [
        {
          content: '{System.current_user}',
          contentType: 'PARAGRAPH',
          style: {
            isVariable: true,
          },
        },
        {
          content: '\n',
          contentType: 'LINE_BREAK',
          style: {
            alignment: 'left',
            lineSpacing: 1.5,
            indent: 0,
          },
        },
      ],
      description: t(LOGIC_LIST_DESC.CURRENT_USER.key, LOGIC_LIST_DESC.CURRENT_USER.value),
    },
    {
      id: 5,
      value: 'custom_logic',
      placeHolderText: 'custom_logic',
      description: t(LOGIC_LIST_DESC.CUSTOM_LOGIC.key, LOGIC_LIST_DESC.CUSTOM_LOGIC.value),
    },
  ], [t]);


  const filteredLogic = useMemo(() => {
    setActive(0);
    let list = LOGIC_LIST.filter(logic =>
      logic.value.toLowerCase().startsWith(searchTerm?.toLowerCase()),
    );

    if (!isEditable) {
      list = list.filter(logic => logic.id !== 4);
    }

    return list;
  }, [searchTerm, isEditable]);
  

  const upHandler = useCallback(() => {
  if (filteredLogic.length === 0) return;
  setActive(prev => (prev === 0 ? filteredLogic.length - 1 : prev - 1));
  setTimeout(() => {
    scrollRef.current.scrollTop = active * 50;
  }, 10);
  }, [filteredLogic, active]);


  const downHandler = useCallback(() => {
  if (filteredLogic.length === 0) return;
  setActive(prev => (prev === filteredLogic.length - 1 ? 0 : prev + 1));
  setTimeout(() => {
    scrollRef.current.scrollTop = active * 50;
  }, 10);
  }, [filteredLogic, active]);


 const escapeHandler = useCallback(() => {
    onHide();
}, [onHide]);


  const enterHandler = useCallback(() => {
  if (filteredLogic.length !== 0) {
    onVariableSelect(filteredLogic[active]);
  }
}, [active, filteredLogic]);


  const isLogicMatched = !!LOGIC_LIST.find(logic =>
    logic.value.toLowerCase().startsWith(searchTerm?.toLowerCase()),
  );

  useEffect(() => {
    if (target === undefined || !isLogicMatched) return;
    const targetEventListener = event => {
      if (event.keyCode === Keys.UP) {
        upHandler();
      } else if (event.keyCode === Keys.DOWN) {
        downHandler();
      } else if (event.keyCode === Keys.ENTER) {
        enterHandler();
      } else if (event.keyCode === Keys.ESCAPE) {
        escapeHandler();
      }
    };
    target.addEventListener('keydown', targetEventListener);
    return () => {
      target.removeEventListener('keydown', targetEventListener);
    };
  }, [target, isLogicMatched, upHandler, downHandler, enterHandler, escapeHandler]);

  useEffect(() => {
    if (quill === undefined || !isLogicMatched) return;
    quill.keyboard.addBinding(
      {
        key: Keys.UP,
      },
      upHandler,
    );

    quill.keyboard.addBinding(
      {
        key: Keys.DOWN,
      },
      downHandler,
    );

    quill.keyboard.addBinding(
      {
        key: Keys.ENTER,
      },
      enterHandler,
    );

    quill.keyboard.bindings[Keys.ENTER].unshift(quill.keyboard.bindings[Keys.ENTER].pop());

    quill.keyboard.addBinding(
      {
        key: Keys.ESCAPE,
      },
      escapeHandler,
    );
    return () => {
      quill.keyboard.bindings[Keys.UP].pop();
      quill.keyboard.bindings[Keys.DOWN].pop();
      quill.keyboard.bindings[Keys.ENTER].shift();
      quill.keyboard.bindings[Keys.ESCAPE].pop();
    };
  }, [quill, isLogicMatched, upHandler, downHandler, enterHandler, escapeHandler]);

  return (
    <div
      style={{
        position: position ? 'absolute' : 'relative',
        left: position ? `${position.left}px` : undefined,
        top: position ? `${position.bottom + 10}px` : undefined,
        zIndex: 10000,
      }}
    >
      {filteredLogic.length !== 0 && (
        <CustomLogicPopupContainer ref={scrollRef} showVariableList={filteredLogic.length !== 0}>
          <Menu>
            {filteredLogic.map((logic, index) => (
              <Menu.Item className={`${index === active && "logic-hover"}`} key={index} id={`menu-item-${logic.id}`}>
                <MenuItem
                  variable={1}
                  active={index === active}
                  onMouseDown={domEvent => {
                    domEvent.preventDefault();
                    domEvent.stopPropagation();
                    onVariableSelect(logic);
                  }}
                >
                  <div className="logic_function_item">
                    <div>{logic.value}</div>
                    <div className="logic_description">{logic.description}</div>
                  </div>
                </MenuItem>
              </Menu.Item>
            ))}
          </Menu>
        </CustomLogicPopupContainer>
      )}
    </div>
  );
};
export default LogicMentionPopup;
